/**
 * Softphone transport contract.
 *
 * The portal owns the softphone UI and state machine; the actual call media is owned by a
 * browser SDK (see ringba-x #2713 / ADR `telnyx-webrtc-credentials-client`, decision #5:
 * the incoming call, answer and hangup all happen inside the browser WebRTC client and
 * Ringba only proxies the credential token). Everything below the store talks to this
 * contract so the SDK can be swapped without touching components or Vuex.
 */

/**
 * Events a transport MUST emit. Payload shapes are documented per event.
 *
 * - `registered`   `{}`                                     transport is connected and eligible for calls
 * - `unregistered` `{ requested }`                          transport is disconnected; see below
 * - `incoming`     `{ callId, campaignName, callerNumber }` an inbound call is ringing
 * - `answered`     `{ callId, callerNumber }`               the call is up (media flowing)
 * - `ended`        `{ callId, reason }`                     the call finished (either side)
 * - `error`        `{ message, code }`                      transport level failure
 *
 * `unregistered.requested` is the difference between an ending and an accident: `true` when the
 * event belongs to a `disconnect()` the application asked for, `false` when the connection dropped
 * underneath it. The store reconnects on one and goes offline on the other, so a transport that
 * reports the wrong flag either abandons a shift that was recoverable or reconnects a shift the
 * agent deliberately ended. A transport whose own teardown makes the underlying SDK emit a close
 * event MUST report that close as requested.
 */
export const SOFTPHONE_EVENTS = Object.freeze({
  REGISTERED: 'registered',
  UNREGISTERED: 'unregistered',
  INCOMING: 'incoming',
  ANSWERED: 'answered',
  ENDED: 'ended',
  ERROR: 'error'
})

/**
 * Commands a transport MUST accept:
 * `connect(token)`, `disconnect()`, `answer(callId)`, `reject(callId)`, `hangup(callId)`,
 * `setMuted(isMuted)`. All are allowed to be async; callers await them.
 *
 * Subclasses implement the commands and call `_emit` for the events above.
 */
export class SoftphoneTransport {
  constructor() {
    this._handlers = {}

    /**
     * Set by `disconnect()` and never cleared - a disconnected transport is discarded, not reused.
     * It is what lets an SDK close event that arrives DURING our own teardown still be reported as
     * requested, whichever order the SDK fires things in.
     */
    this.isDisconnectRequested = false
  }

  on(eventName, handler) {
    if (!this._handlers[eventName]) {
      this._handlers[eventName] = []
    }

    this._handlers[eventName].push(handler)

    return this
  }

  off(eventName, handler) {
    if (!this._handlers[eventName]) {
      return this
    }

    this._handlers[eventName] = this._handlers[eventName].filter(h => h !== handler)

    return this
  }

  removeAllListeners() {
    this._handlers = {}
  }

  _emit(eventName, payload = {}) {
    const handlers = this._handlers[eventName] || []

    handlers.forEach(handler => handler(payload))
  }

  /**
   * The one event with a payload that is easy to get wrong, so subclasses do not build it by hand.
   * Called with no argument it answers from `isDisconnectRequested`, which is the right answer for
   * an SDK close event: requested when it is part of our teardown, a drop when it is not.
   */
  _emitUnregistered({ requested } = {}) {
    this._emit(SOFTPHONE_EVENTS.UNREGISTERED, {
      requested: requested === undefined ? this.isDisconnectRequested === true : requested === true
    })
  }

  // eslint-disable-next-line no-unused-vars
  async connect(token) {
    throw new Error('SoftphoneTransport.connect is not implemented')
  }

  async disconnect() {
    throw new Error('SoftphoneTransport.disconnect is not implemented')
  }

  // eslint-disable-next-line no-unused-vars
  async answer(callId) {
    throw new Error('SoftphoneTransport.answer is not implemented')
  }

  // eslint-disable-next-line no-unused-vars
  async reject(callId) {
    throw new Error('SoftphoneTransport.reject is not implemented')
  }

  // eslint-disable-next-line no-unused-vars
  async hangup(callId) {
    throw new Error('SoftphoneTransport.hangup is not implemented')
  }

  // eslint-disable-next-line no-unused-vars
  async setMuted(isMuted) {
    throw new Error('SoftphoneTransport.setMuted is not implemented')
  }
}

export default SoftphoneTransport
